import RcSlider from 'rc-slider'
import React, { useRef, useState } from 'react'

import { Icon } from '@fourthwall/components'
import { PauseFullIcon, PlayFullIcon, VolumeOnFullIcon } from '@fourthwall/icons'

import { millisecondsToTime } from '../../utils/millisecondsToTime'
import * as S from './AudioPlayer.styled'

export type ActionButtonProps = {
  src: string
}

export const AudioPlayer = ({ src }: ActionButtonProps) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const withHours = duration >= 3600

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current?.duration)
      setVolume(audioRef.current.volume)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handlePlay = () => {
    audioRef.current?.play()
    setIsPlaying(true)
  }

  const handlePause = () => {
    audioRef.current?.pause()
    setIsPlaying(false)
  }

  const handleCurrentTimeChange = (value: number | number[]) => {
    if (Array.isArray(value) || !audioRef.current) return

    if (!audioRef.current.paused) {
      handlePause()
    }

    audioRef.current.currentTime = value
    setCurrentTime(value)
  }

  const handleVolumeChange = (value: number | number[]) => {
    if (Array.isArray(value) || !audioRef.current) return

    audioRef.current.volume = value
    setVolume(value)
  }

  const handleVolumeToggle = () => {
    if (!audioRef.current) return

    const newVolume = audioRef.current?.volume < 1 ? 1 : 0

    audioRef.current.volume = newVolume
    setVolume(newVolume)
  }

  return (
    <S.Container>
      <S.AudioPlayerTrack>
        <RcSlider
          min={0}
          max={duration}
          step={0.01}
          value={currentTime}
          onChange={handleCurrentTimeChange}
        />
      </S.AudioPlayerTrack>
      <S.AudioPlayerControls>
        <S.AudioPlayerControl
          paddingLeft={isPlaying ? 0 : 2}
          onClick={isPlaying ? handlePause : handlePlay}
        >
          <Icon component={isPlaying ? PauseFullIcon : PlayFullIcon} height={16} />
        </S.AudioPlayerControl>

        <S.AudioPlayerControlsTimer>
          <S.Time>{millisecondsToTime(currentTime * 1000, { withHours })}</S.Time>
          <div>/</div>
          <S.Time>{millisecondsToTime(duration * 1000, { withHours })}</S.Time>
        </S.AudioPlayerControlsTimer>

        <S.AudioPlayerControl onClick={handleVolumeToggle}>
          <Icon component={VolumeOnFullIcon} height={16} />
        </S.AudioPlayerControl>

        <S.AudioPlayerAudio>
          <S.AudioPlayerTrack>
            <RcSlider min={0} max={1} step={0.001} value={volume} onChange={handleVolumeChange} />
          </S.AudioPlayerTrack>
        </S.AudioPlayerAudio>
      </S.AudioPlayerControls>

      <S.AudioElement
        ref={audioRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handlePause}
      />
    </S.Container>
  )
}
